import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useOffer,
  useOfferLines,
  useOfferLineTemplates,
  type OfferLine,
} from "@/hooks/use-offers";
import { useOfferSections } from "@/hooks/use-offer-sections";
import { useSystemConfigNumber } from "@/hooks/use-system-config";
import { useOfferEditState } from "@/hooks/use-offer-edit-state";
import { useOfferLineActions } from "@/hooks/use-offer-line-actions";
import { useOfferKeyboardShortcuts } from "@/hooks/use-offer-keyboard-shortcuts";
import { useOfferEditors } from "@/hooks/use-offer-editors";
import { useOfferBudgetImageCopy } from "@/hooks/use-offer-budget-image-copy";
import { useOfferPageHandlers } from "@/hooks/use-offer-page-handlers";
import { OfferEditHeader } from "@/components/offer/OfferEditHeader";
import { OfferLineTable } from "@/components/offer/OfferLineTable";
import { OfferEditFooter } from "@/components/offer/OfferEditFooter";
import { OfferBudgetImage } from "@/components/offer/OfferBudgetImage";
import { OfferEditDialogs } from "@/components/offer/OfferEditDialogs";
import { OfferReadOnlyBanner } from "@/components/offer/OfferReadOnlyBanner";
import { LostReasonDialog } from "@/components/offer/LostReasonDialog";
import { SendOfferDialog } from "@/components/offer/SendOfferDialog";
import { useOfferStatusRequest } from "@/hooks/use-offer-status-request";
import { OfferWorkspaceBranches } from "@/components/tilbudsarbeidsrom/OfferWorkspaceBranches";
import { parseBranch } from "@/lib/tilbudsarbeidsrom/branches";
import { OfferLostReasonBanner } from "@/components/offer/OfferLostReasonBanner";
import { SrIcon } from "@/components/ui/SrIcon";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useRBAC } from "@/hooks/use-rbac";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { buildTemplateHourlyRates, resolveLineRate } from "@/lib/editable-line";

export default function OfferEditPage() {
  const { id, gren } = useParams<{ id: string; gren?: string }>();

  const { activeUser } = useImpersonation();
  const { isAdmin } = useRBAC();

  const { data: offer, isLoading: offerLoading } = useOffer(id);
  const { data: lines = [], isLoading: linesLoading } = useOfferLines(id);
  const { data: sections = [] } = useOfferSections(id);
  const { data: templates = [] } = useOfferLineTemplates();
  const { data: systemHourlyRate = 2000 } = useSystemConfigNumber("hourly_rate");

  // VIB-2097: katalogens timesats per linje (mal → kr/t, dagmal ÷ 8). Er
  // fallback-sats og tidsbasis når en fast-linje byttes tilbake til t/d.
  const rateByTemplate = useMemo(() => buildTemplateHourlyRates(templates), [templates]);
  const rateForLine = (line: OfferLine) =>
    resolveLineRate(line.template_id, rateByTemplate, systemHourlyRate);
  const overtimeRateForLine = (line: OfferLine) => {
    const t = templates.find((x) => x.id === line.template_id);
    return t?.default_overtime_rate ?? 0;
  };

  // VIB-1304: hele salgspipeline-pakken (kategori/board/KPI/varsel/lost_reason)
  // bak ETT flagg (Erik 18.07). Når av: identisk med pre-VIB-1304-editoren.
  // CRM-2.7: Salgsmuligheter krever sannsynlighet, close-dato og tapsårsak på tilbud for
  // at Pipe skal gi mening, så pipeline-feltene gjelder når ENTEN tilbudspipen ELLER
  // CRM kø og pipe er på. Samme regel i useOfferStatusChange via useOfferPageHandlers.
  const offerPipelineFlag = useFeatureFlag("feature_offer_pipeline");
  const crmKoPipeFlag = useFeatureFlag("feature_crm_ko_pipe");
  const pipelineEnabled = offerPipelineFlag || crmKoPipeFlag;
  // VIB-2979: grenene i tilbudsarbeidsrommet. Av = dagens side uten grener.
  const workspaceEnabled = useFeatureFlag("feature_offer_workspace");
  const branch = workspaceEnabled ? parseBranch(gren) : "budsjett";

  const { data: editors = [] } = useOfferEditors(id);
  const isOwner = !!offer && offer.created_by === activeUser?.id;
  const canManage = isAdmin || isOwner;
  const canEdit = canManage || editors.some((e) => e.user_id === activeUser?.id);
  const readOnly = !canEdit;

  // Draft-state + computed totals + auto-save + save-handler (VIB-658-split)
  const {
    draft,
    setDraft,
    hourlyRate,
    totals,
    canSave,
    isSaving,
    save: handleSave,
  } = useOfferEditState({ offer, lines, systemHourlyRate, canEdit });

  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  /**
   * VIB-663e-fix: hvilken seksjon katalog-dialogen ble åpnet for. null =
   * hovedbudsjett, string = en bestemt tilvalgsseksjon. Plukkede linjer
   * får `section_id` satt til denne verdien.
   */
  const [catalogTargetSectionId, setCatalogTargetSectionId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false);
  /** Brukes for å auto-fokusere mengde-cellen på sist lagt-til linje. */
  const [autoFocusLineId, setAutoFocusLineId] = useState<string | null>(null);
  /** Mobile edit-sheet: hvilken linje redigeres (null = sheet lukket). */
  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  // Page-level handlers: terms, status, delete, export Keynote (VIB-658-split).
  const {
    isExportingPptx,
    handleMainTermsTextChange,
    handleSectionTermsTextChange,
    handleDeleteSection,
    handleStatusChange,
    handleConfirmLost,
    handleConfirmSend,
    handleProbabilityChange,
    handleValidUntilChange,
    handleDelete,
    handleDuplicate,
    isDuplicating,
    handleExportPptx,
  } = useOfferPageHandlers({ offer, lines, sections, canEdit, canManage, pipelineEnabled });

  const {
    lostReasonDialogOpen,
    setLostReasonDialogOpen,
    sendDialogOpen,
    setSendDialogOpen,
    requestStatusChange: handleStatusChangeRequest,
  } = useOfferStatusRequest({ offer, pipelineEnabled, onStatusChange: handleStatusChange });

  // "Kopier som bilde"-flyt: ref + font/pageSlice-state. Store budsjett
  // pagineres i lesbar størrelse i stedet for å krympes (VIB-2091).
  const {
    budgetImageRef,
    budgetFontSize,
    budgetTwoColumns,
    budgetPageSlice,
    copyingSectionKey,
    handleCopySectionImage,
  } = useOfferBudgetImageCopy({ sections, lines, fileBaseName: offer?.title ?? "Budsjett" });

  // Line-handlers (template-pick, update, delete, reorder, move) — VIB-658-split
  const {
    handlePickTemplate,
    handleUpdateLine,
    handleDeleteLine,
    handleReorder,
    handleMoveLine,
    handleDuplicateLine,
  } = useOfferLineActions({
    offerId: offer?.id ?? "",
    lines,
    hourlyRate: hourlyRate ?? 2000,
    catalogTargetSectionId,
    onLineCreated: setAutoFocusLineId,
    canEdit,
  });

  const editingLine = useMemo(
    () => (editingLineId ? lines.find((l) => l.id === editingLineId) ?? null : null),
    [lines, editingLineId],
  );

  useOfferKeyboardShortcuts({
    sections,
    canEdit,
    canSave,
    onSave: handleSave,
    onOpenCatalog: () => {
      setCatalogTargetSectionId(null);
      setCatalogDialogOpen(true);
    },
    onOpenCatalogForSection: (sectionId) => {
      setCatalogTargetSectionId(sectionId);
      setCatalogDialogOpen(true);
    },
    onOpenKeyboardHelp: () => setKeyboardHelpOpen(true),
    onDuplicateLine: handleDuplicateLine,
    onMoveLine: handleMoveLine,
  });

  if (offerLoading || !offer || !draft) {
    return (
      <div className="page-container flex items-center justify-center py-16">
        <SrIcon name="loader2" className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  function handleLineTap(lineId: string) {
    setEditingLineId(lineId);
  }

  function handleSheetSave(patch: Partial<OfferLine>) {
    if (!editingLineId) return;
    handleUpdateLine(editingLineId, patch);
  }

  function handleSheetDelete() {
    if (!editingLineId) return;
    handleDeleteLine(editingLineId);
    setEditingLineId(null);
  }

  const customerLabel = offer.customer?.name ?? offer.customer_name ?? "—";

  return (
    <div className="page-container animate-fade-in pb-8">
      {readOnly && <OfferReadOnlyBanner />}

      <OfferEditHeader
        offer={offer}
        customerLabel={customerLabel}
        draftTitle={draft.title}
        readOnly={readOnly}
        canManage={canManage}
        canSave={canSave}
        isSaving={isSaving}
        isExportingPptx={isExportingPptx}
        hasLines={lines.length > 0}
        hasProject={!!offer.project_id}
        onTitleChange={(next) => setDraft({ ...draft, title: next })}
        onSave={handleSave}
        onStatusChange={handleStatusChangeRequest}
        onConvertRequest={() => setConvertConfirmOpen(true)}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        isDuplicating={isDuplicating}
        onExportPptx={handleExportPptx}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenKeyboardHelp={() => setKeyboardHelpOpen(true)}
        pipelineEnabled={pipelineEnabled}
        onProbabilityChange={handleProbabilityChange}
        onValidUntilChange={handleValidUntilChange}
      />

      {pipelineEnabled && <OfferLostReasonBanner offer={offer} />}

      <LostReasonDialog
        open={lostReasonDialogOpen}
        onOpenChange={setLostReasonDialogOpen}
        offerTitle={offer.title}
        onConfirm={(reason) => {
          handleConfirmLost(reason);
          setLostReasonDialogOpen(false);
        }}
      />

      <SendOfferDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        offerTitle={offer.title}
        currentProbability={offer.probability}
        currentExpectedCloseDate={offer.expected_close_date}
        onConfirm={(fields) => {
          handleConfirmSend(fields);
          setSendDialogOpen(false);
        }}
      />

      <OfferWorkspaceBranches
        enabled={workspaceEnabled}
        branch={branch}
        offer={offer}
        lineCount={lines.length}
        hourlyRate={hourlyRate ?? 2000}
        customerLabel={customerLabel}
        description={draft.description}
        onDescriptionChange={(description) => setDraft({ ...draft, description })}
        readOnly={readOnly}
      >
      {linesLoading ? (
        <div className="flex justify-center py-8">
          <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <OfferLineTable
          lines={lines}
          sections={sections}
          readOnly={readOnly}
          hourlyRate={hourlyRate ?? 2000}
          rateForLine={rateForLine}
          overtimeRateForLine={overtimeRateForLine}
          autoFocusLineId={autoFocusLineId}
          copyingSectionKey={
            copyingSectionKey === undefined
              ? null
              : copyingSectionKey === null
                ? "main"
                : copyingSectionKey
          }
          offerTotals={{
            overhead_pct: draft.overhead_pct,
            discount_pct: draft.discount_pct,
            discount_amount: draft.discount_amount,
            include_travel_in_overhead: offer.include_travel_in_overhead,
          }}
          mainTermsText={offer.custom_terms_text ?? ""}
          onMainTermsTextChange={handleMainTermsTextChange}
          onSectionTermsTextChange={handleSectionTermsTextChange}
          onChange={handleUpdateLine}
          onDelete={handleDeleteLine}
          onAddLine={(sectionId) => {
            setCatalogTargetSectionId(sectionId);
            setCatalogDialogOpen(true);
          }}
          onAddSection={() => setAddSectionOpen(true)}
          onDeleteSection={handleDeleteSection}
          onCopySectionImage={handleCopySectionImage}
          onReorder={handleReorder}
          onDuplicate={handleDuplicateLine}
          onLineTap={handleLineTap}
          onMoveLine={handleMoveLine}
        />
      )}

      <OfferEditFooter
        totals={totals}
        readOnly={readOnly}
        overheadPct={draft.overhead_pct}
        discountPct={draft.discount_pct}
        discountAmount={draft.discount_amount}
        hourlyRate={hourlyRate}
        targetHourlyRate={systemHourlyRate}
        onChange={(patch) =>
          setDraft({
            ...draft,
            ...(patch.overhead_pct !== undefined ? { overhead_pct: patch.overhead_pct } : {}),
            ...(patch.discount_pct !== undefined ? { discount_pct: patch.discount_pct } : {}),
            ...(patch.discount_amount !== undefined ? { discount_amount: patch.discount_amount } : {}),
          })
        }
      />
      </OfferWorkspaceBranches>

      <OfferEditDialogs
        offer={offer}
        lines={lines}
        sections={sections}
        templates={templates}
        hourlyRate={hourlyRate ?? 2000}
        rateForLine={rateForLine}
        catalogDialogOpen={catalogDialogOpen}
        onCatalogOpenChange={(open) => {
          setCatalogDialogOpen(open);
          // Reset target når dialogen lukkes så neste ⌘L fra header
          // ikke arver en gammel seksjon.
          if (!open) setCatalogTargetSectionId(null);
        }}
        onPickTemplate={handlePickTemplate}
        convertConfirmOpen={convertConfirmOpen}
        onConvertConfirmOpenChange={setConvertConfirmOpen}
        onConvertConfirm={() => {
          setConvertConfirmOpen(false);
          setConvertOpen(true);
        }}
        convertOpen={convertOpen}
        onConvertOpenChange={setConvertOpen}
        settingsOpen={settingsOpen}
        onSettingsOpenChange={setSettingsOpen}
        addSectionOpen={addSectionOpen}
        onAddSectionOpenChange={setAddSectionOpen}
        addSectionNextSortOrder={
          sections.length === 0
            ? 10
            : Math.max(...sections.map((s) => s.sort_order)) + 10
        }
        keyboardHelpOpen={keyboardHelpOpen}
        onKeyboardHelpOpenChange={setKeyboardHelpOpen}
        editingLine={editingLine}
        editingLineId={editingLineId}
        onEditingLineOpenChange={(open) => {
          if (!open) setEditingLineId(null);
        }}
        onSheetSave={handleSheetSave}
        onSheetDelete={handleSheetDelete}
      />

      {/* Skjult render-mål for "Kopier som bilde" */}
      <div
        aria-hidden="true"
        style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }}
      >
        <OfferBudgetImage
          ref={budgetImageRef}
          offer={offer}
          lines={lines}
          sections={sections}
          fontSize={budgetFontSize}
          twoColumns={budgetTwoColumns}
          exportSectionKey={copyingSectionKey}
          pageSlice={budgetPageSlice}
        />
      </div>
    </div>
  );
}
